// ABOUTME: Hook for dynamic z-index management in portals/modals/sidebars
// ABOUTME: Returns a stable z-index that's assigned when component opens

import { useState, useEffect } from "react"
import { getNextZIndex } from "@shared/lib/zIndex"

/**
 * Hook that returns a z-index value for portal-based components.
 * The z-index is assigned when isOpen becomes true and stays stable
 * until the component closes.
 *
 * @param isOpen - Whether the component is currently open/visible
 * @returns z-index number to use for positioning
 *
 * @example
 * const MyModal = ({ isOpen }) => {
 *   const zIndex = useZIndex(isOpen)
 *   if (!isOpen) return null
 *   return createPortal(
 *     <div style={{ zIndex }}>...</div>,
 *     document.body
 *   )
 * }
 */
export const useZIndex = (isOpen: boolean): number => {
  const [zIndex, setZIndex] = useState(0)

  useEffect(() => {
    if (isOpen && zIndex === 0) {
      setZIndex(getNextZIndex())
    } else if (!isOpen) {
      setZIndex(0)
    }
  }, [isOpen, zIndex])

  return zIndex
}
